import { type FC } from "react";
import { ReceiptText, Landmark, CircleHelp } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { useIntercom } from "@/shared/providers/IntercomProvider";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogBody,
} from "@/shared/components/ui/dialog";

interface EstateExpenseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EstateExpenseModal: FC<EstateExpenseModalProps> = ({ open, onOpenChange }) => {
  const { openSidePanel } = useIntercom();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogTitle className="sr-only">Paying for Elayne from the estate</DialogTitle>
        <DialogHeader>
          <div className="flex flex-col gap-2">
            <h3>Paying for Elayne from the estate</h3>
            <p className="text-sm text-secondary">
              Elayne is an eligible estate expense, which means the cost can come from the estate
              itself, not your personal funds. There are two ways this works:
            </p>
          </div>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex flex-1 flex-col gap-4 rounded-lg border border-primary bg-subtle p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-md border border-primary bg-white">
                <ReceiptText className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-2xs font-semibold uppercase tracking-widest text-secondary">
                  Option 1
                </p>
                <p className="text-base font-medium">You pay now, get reimbursed</p>
                <p className="text-sm text-secondary">
                  If the estate account isn&apos;t open yet, you can pay now and reimburse yourself
                  from the estate once you have legal authority and access to estate funds.
                </p>
              </div>
              <p className="text-2xs text-secondary">
                We&apos;ll keep documentation of the expense for your records.
              </p>
            </div>

            <div className="flex flex-1 flex-col gap-4 rounded-lg border border-primary bg-subtle p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-md border border-primary bg-white">
                <Landmark className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-2xs font-semibold uppercase tracking-widest text-secondary">
                  Option 2
                </p>
                <p className="text-base font-medium">We bill the estate directly</p>
                <p className="text-sm text-secondary">
                  If the estate account is already open, you can pay directly from estate funds at
                  checkout.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex flex-1 items-center gap-2">
              <CircleHelp className="h-4 w-4 shrink-0 text-secondary" />
              <p className="text-sm">
                More questions?{" "}
                <button type="button" className="text-brand underline" onClick={openSidePanel}>
                  Chat with our team
                </button>
              </p>
            </div>
            <Button variant="brand" onClick={() => onOpenChange(false)}>
              Got it
            </Button>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
